import { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
} from 'melp-design/components';
import { Link } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDialog } from 'utils/Dialog';
import { usePointsLabel } from 'store/recognitions';
import {
  RecognitionAllowanceDetailsFilters,
  recognitionAllowanceDetailsFiltersSchema,
  useRecognitionAllowanceDetails,
} from 'store/recognition-allowances';

interface Props {
  employeeId: string;
}

const Content: FC<Props> = ({ employeeId }) => {
  const { t } = useTranslation();
  const pointsLabel = usePointsLabel();

  const [filters, setFilters] = useState<RecognitionAllowanceDetailsFilters>(
    recognitionAllowanceDetailsFiltersSchema.parse({}),
  );

  const { data: details, isLoading } = useRecognitionAllowanceDetails(
    employeeId,
    filters,
  );

  return (
    <>
      <DialogTitle>
        {t('recognition.allowanceOverviewAssignedAllowanceDetailsTitle')}
      </DialogTitle>
      <DialogContent>
        <Table
          data={details?.items ?? []}
          isLoading={isLoading}
          columns={[
            {
              id: 'pointsReceived',
              header: t(
                'recognition.allowanceEmployeeOverviewListPointsReceived',
                { recognitionCurrency: pointsLabel },
              ),
              cell: ({ row: { original: detail } }) => detail.pointsReceived,
            },
            {
              id: 'pointsSpent',
              header: t(
                'recognition.allowanceEmployeeOverviewListPointsSpent',
                {
                  recognitionCurrency: pointsLabel,
                },
              ),
              cell: ({ row: { original: detail } }) => detail.pointsSpent,
            },
            {
              id: 'validFrom',
              header: t('recognition.allowanceEmployeeOverviewListValidFrom'),
              cell: ({ row: { original: detail } }) =>
                detail.validFrom ? moment(detail.validFrom).format('l') : '-',
            },
            {
              id: 'validTo',
              header: t('recognition.allowanceEmployeeOverviewListValidTo'),
              cell: ({ row: { original: detail } }) =>
                detail.validTo ? moment(detail.validTo).format('l') : '-',
            },
            {
              id: 'frequency',
              header: t('recognition.allowanceEmployeeOverviewListFrequency'),
              cell: ({ row: { original: detail } }) =>
                t(`recognition.allowanceRuleFrequency-${detail.frequency}`),
            },
            {
              id: 'status',
              header: t('common.status'),
              cell: ({ row: { original: detail } }) => {
                return moment().isAfter(detail.validTo)
                  ? t('recognition.allowanceStatus-expired')
                  : t('recognition.allowanceStatus-valid');
              },
            },
          ]}
          rows={{
            inactive: ({ original: detail }) =>
              moment().isAfter(detail.validTo),
          }}
          pagination={
            details
              ? {
                  count: details.total,
                  page: details.page,
                  pageSize: details.pageSize,
                  onChange: (page, pageSize) => {
                    setFilters({ page, pageSize });
                  },
                }
              : undefined
          }
        />
      </DialogContent>
    </>
  );
};

const AssignedAllowanceDetails = (props: Props) => {
  const { t } = useTranslation();
  const allowanceDetailsDialog = useDialog();

  return (
    <>
      <Link component="button" onClick={allowanceDetailsDialog.openDialog}>
        {t('recognition.allowanceOverviewAssignedAllowanceDetailsLink')}
      </Link>
      <Dialog
        open={allowanceDetailsDialog.open}
        onClose={allowanceDetailsDialog.closeDialog}
        width={800}
      >
        <Content {...props} />
      </Dialog>
    </>
  );
};

export default AssignedAllowanceDetails;
